import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, AlertTriangle, RefreshCw, LogOut, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import {
  googleBusinessProfileService,
  type AccountLocationPair,
  type GbpConnectionStatus
} from '@/services/googleBusinessProfileService';

const SECTION_TITLE = 'Google Business Profile';

// Centralized error-toast logic for every GBP action. The service's
// rethrowWithServerCode unwraps the edge-function body into Error.message, so
// here we branch on known codes ('forbidden'/'unauthorized' from gbp-auth,
// 'oauth_not_configured' from gbp-oauth-start, 'connect_in_progress' from a
// concurrent insert) and fall back to the action-specific generic message.
function showGbpActionError(err: unknown, fallback: string): void {
  const code = err instanceof Error ? err.message : '';
  if (code === 'forbidden' || code === 'unauthorized') {
    toast.error('You do not have permission to manage Google Business Profile. Admin role required.');
  } else if (code === 'oauth_not_configured') {
    toast.error(
      'Google OAuth is not configured. Set GBP_OAUTH_CLIENT_ID and GBP_OAUTH_CLIENT_SECRET in Supabase secrets.'
    );
  } else if (code === 'connect_in_progress') {
    toast.error('A connection is already in progress. Refresh and try again.');
  } else {
    toast.error(fallback);
  }
}

export const GoogleBusinessProfileSettings: React.FC = () => {
  const [status, setStatus] = useState<GbpConnectionStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [pairs, setPairs] = useState<AccountLocationPair[] | null>(null);
  const [loadingPairs, setLoadingPairs] = useState(false);
  const [awaitingApproval, setAwaitingApproval] = useState(false);
  const [selectedPair, setSelectedPair] = useState<AccountLocationPair | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const finalizeFlag = searchParams.get('finalize') === 'gbp';

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const next = await googleBusinessProfileService.getStatus();
      setStatus(next);
    } catch {
      toast.error('Failed to load Google Business Profile status');
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const loadPairs = useCallback(async () => {
    setLoadingPairs(true);
    setAwaitingApproval(false);
    try {
      const result = await googleBusinessProfileService.listLocations();
      if (result.awaitingApproval) {
        setAwaitingApproval(true);
        setPairs([]);
      } else {
        setPairs(result.pairs ?? []);
      }
    } catch (err) {
      showGbpActionError(err, 'Failed to list Google Business Profile locations');
      setPairs([]);
    } finally {
      setLoadingPairs(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  // Populate the picker any time the connection is in pending_location state and
  // we haven't loaded pairs yet. This covers both the immediate post-OAuth return
  // (?finalize=gbp present) and a later return where the admin opens the page
  // without the query param.
  useEffect(() => {
    if (status?.status !== 'pending_location') {
      return;
    }
    if (pairs === null && !loadingPairs) {
      void loadPairs();
    }
  }, [status?.status, pairs, loadingPairs, loadPairs]);
  // finalizeFlag is intentionally not a dependency — its only purpose is to be
  // cleared from the URL after a successful finalize (see handleFinalize).
  void finalizeFlag;

  const handleConnect = async () => {
    setSubmitting(true);
    try {
      const { url } = await googleBusinessProfileService.startConnect();
      window.location.href = url;
    } catch (err) {
      showGbpActionError(err, 'Failed to start Google connection');
      setSubmitting(false);
    }
  };

  const handleFinalize = async () => {
    if (!selectedPair) {
return;
}
    setSubmitting(true);
    try {
      await googleBusinessProfileService.finalizeLocation({
        account_resource_name: selectedPair.account.resource_name,
        location_resource_name: selectedPair.location.resource_name
      });
      toast.success('Google Business Profile connected');
      // Clear finalize flag from URL
      const next = new URLSearchParams(searchParams);
      next.delete('finalize');
      setSearchParams(next, { replace: true });
      await loadStatus();
    } catch (err) {
      showGbpActionError(err, 'Failed to save the selected location');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisconnect = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Disconnect Google Business Profile? You will need to reconnect to sync new reviews.')) {
      return;
    }
    setSubmitting(true);
    try {
      await googleBusinessProfileService.disconnect();
      toast.success('Google Business Profile disconnected');
      await loadStatus();
    } catch (err) {
      showGbpActionError(err, 'Failed to disconnect');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const result = await googleBusinessProfileService.verify();
      if (result.verified) {
        toast.success('Connection is healthy');
      } else if (result.reconnectRequired) {
        toast.error(`Reconnection required: ${  result.message ?? ''}`);
      } else {
        toast.error('Verification failed');
      }
      await loadStatus();
    } catch (err) {
      showGbpActionError(err, 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const headerBlock = (
    <div>
      <h1 className="text-3xl font-bold text-foreground">Google Business Profile</h1>
      <p className="text-muted-foreground">
        Connect a Google Business Profile listing to enable review sync, public display, and review replies.
      </p>
    </div>
  );

  if (loadingStatus) {
    return (
      <div className="space-y-6">
        {headerBlock}
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      </div>
    );
  }

  // Reconnect required banner takes precedence
  const renderReconnectBanner = () =>
    status?.needsReconnect && (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" /> Reconnection required
          </CardTitle>
          <CardDescription>
            Your Google connection has expired or been revoked. Reconnect to continue syncing reviews.
            {status.lastRefreshError && (
              <span className="block mt-2 text-sm text-muted-foreground">
                Last error: {status.lastRefreshError}
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleConnect} disabled={submitting}>
            Reconnect
          </Button>
        </CardContent>
      </Card>
    );

  // Active state
  if (status?.status === 'active' && !status.needsReconnect) {
    return (
      <div className="space-y-6">
        {headerBlock}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Connected
            </CardTitle>
            <CardDescription>
              {SECTION_TITLE} is connected and active.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {status.accountDisplayName && (
              <div className="text-sm">
                <span className="text-muted-foreground">Account: </span>
                <span className="font-medium">{status.accountDisplayName}</span>
              </div>
            )}
            {status.locationDisplayName && (
              <div className="text-sm">
                <span className="text-muted-foreground">Location: </span>
                <span className="font-medium">{status.locationDisplayName}</span>
              </div>
            )}
            {status.connectedAt && (
              <div className="text-sm text-muted-foreground">
                Connected {new Date(status.connectedAt).toLocaleString()}
              </div>
            )}
            {status.lastValidatedAt && (
              <div className="text-sm text-muted-foreground">
                Last validated {new Date(status.lastValidatedAt).toLocaleString()}
              </div>
            )}
            <div className="flex gap-2 pt-3">
              <Button variant="outline" onClick={handleVerify} disabled={verifying}>
                {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                <span className="ml-2">Verify now</span>
              </Button>
              <Button variant="outline" onClick={handleDisconnect} disabled={submitting}>
                <LogOut className="h-4 w-4 mr-2" />
                Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Pending location picker
  if (status?.status === 'pending_location') {
    return (
      <div className="space-y-6">
        {headerBlock}
        {renderReconnectBanner()}
        <Card>
          <CardHeader>
            <CardTitle>Choose your location</CardTitle>
            <CardDescription>
              Select the Google Business Profile listing to connect.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {loadingPairs ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading locations…
              </div>
            ) : awaitingApproval ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm">
                Google returned a permission error. This usually means the Business Profile API
                approval is still pending. Try the picker again once approval lands.
              </div>
            ) : pairs && pairs.length > 0 ? (
              <div className="space-y-2">
                {pairs.map((pair) => {
                  const isSelected = selectedPair?.location.resource_name === pair.location.resource_name;
                  return (
                    <button
                      key={pair.location.resource_name}
                      type="button"
                      onClick={() => setSelectedPair(pair)}
                      className={`w-full text-left p-3 rounded-md border flex items-center justify-between hover:bg-muted/40 ${
                        isSelected ? 'border-primary ring-1 ring-primary' : ''
                      }`}
                    >
                      <div>
                        <div className="font-medium">{pair.location.display_name}</div>
                        <div className="text-xs text-muted-foreground">{pair.account.display_name}</div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                No locations available. Try reconnecting.
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button onClick={handleFinalize} disabled={!selectedPair || submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save selection
              </Button>
              <Button variant="outline" onClick={loadPairs} disabled={loadingPairs}>
                Refresh locations
              </Button>
              <Button variant="ghost" onClick={handleDisconnect} disabled={submitting}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Disconnected (with banner) OR not connected at all
  return (
    <div className="space-y-6">
      {headerBlock}
      {renderReconnectBanner()}
      {!status?.needsReconnect && (
        <Card>
          <CardHeader>
            <CardTitle>Not connected</CardTitle>
            <CardDescription>
              Connect a Google Business Profile to enable review sync, public display, and review replies.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleConnect} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Connect Google Business Profile
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GoogleBusinessProfileSettings;
